import axios, { AxiosInstance, AxiosResponse } from "axios";
import { Modal, message } from "antd";
import RequestConfig from "./request-config";
import MainUtils from "../main";
import {
  AUTH_TOKEN,
  Default_Language,
  TOKEN,
  REFRESH_TOKEN,
  remoteServiceBaseApiUrl,
} from "../../constants/AppConst";
import urlJoin from "url-join";

export interface IBaseApiResponse<T> {
  data: T;
}

export default class ApiProvider {
  api: AxiosInstance;

  getCookieValue(key: string): string | null {
    var equalities = document.cookie.split("; ");
    for (var i = 0; i < equalities.length; i++) {
      if (!equalities[i]) {
        continue;
      }

      var splitted = equalities[i].split("=");
      if (splitted.length !== 2) {
        continue;
      }

      if (decodeURIComponent(splitted[0]) === key) {
        return decodeURIComponent(splitted[1] || "");
      }
    }

    return null;
  }
  deleteCookie(name: string) {
    const date = new Date();
    date.setTime(date.getTime() + -1 * 24 * 60 * 60 * 1000);
    document.cookie = name + "=; expires=" + date.toUTCString() + "; path=/";
  }

  constructor(config: RequestConfig) {
    this.api = axios.create({
      ...config,
      baseURL: urlJoin(
        `${remoteServiceBaseApiUrl}`,
        "api",
        `${config.baseURL}`,
      ),
    });
    this.api.interceptors.request.use((req: any) => {
      return {
        ...req,
        headers: {
          ...req.headers,
          Accept: "*",
          Authorization: `${this.getCookieValue(TOKEN) ?? this.getCookieValue(AUTH_TOKEN)}`,
          ["language-key"]: `${this.getCookieValue(
            Default_Language,
          )?.toUpperCase()}`,
        },
      };
    });
    let isRefreshing = false;
    let refreshSubscribers: Array<(token: string) => void> = [];

    const subscribeTokenRefresh = (cb: (token: string) => void) => {
      refreshSubscribers.push(cb);
    };
    const onRefreshed = (token: string) => {
      refreshSubscribers.forEach((cb) => cb(token));
      refreshSubscribers = [];
    };

    this.api.interceptors.response.use(
      (res: AxiosResponse) => {
        if (res.data === "") {
          return { ...res, data: null };
        }
        if (
          typeof res?.data?.data === "string" &&
          res?.config?.url !== "/set-web-fcm-token"
        ) {
          message.success(res?.data?.Data);
        }
        return res?.data;
      },
      async (error: any) => {
        const originalRequest = error?.config;
        const status =
          error?.response?.status ?? error?.response?.data?.StatusCode;

        if (status === 401 && !originalRequest?._retry) {
          originalRequest._retry = true;
          if (!isRefreshing) {
            isRefreshing = true;
            try {
              const refreshToken = this.getCookieValue(REFRESH_TOKEN);
              if (!refreshToken) throw new Error("No refresh token");

              // Direct axios call to avoid circular dependency
              const resp = await axios.post(
                urlJoin(remoteServiceBaseApiUrl, "api", "Auth", "RefreshToken"),
                { refreshToken },
                {
                  headers: {
                    Accept: "*",
                    Authorization: `${this.getCookieValue(TOKEN) ?? this.getCookieValue(AUTH_TOKEN)}`,
                  },
                },
              );

              const newToken = resp?.data?.Data?.token;
              const expireDate = resp?.data?.Data?.expireDate;
              if (!newToken) throw new Error("No new token");
              document.cookie = `${AUTH_TOKEN}=${newToken}; path=/;${expireDate ? ` expires=${new Date(expireDate).toUTCString()};` : ""}`;
              isRefreshing = false;
              onRefreshed(newToken);
            } catch (e) {
              isRefreshing = false;
              this.deleteCookie(AUTH_TOKEN);
              this.deleteCookie(REFRESH_TOKEN);
              window.location.href = "/error-pages/error-401";
              return Promise.reject(error);
            }
          }

          return new Promise((resolve) => {
            subscribeTokenRefresh((token: string) => {
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: token,
              };
              resolve(this.api.request(originalRequest));
            });
          });
        }

        if (status === 403) return { error: error?.response?.data };
        if (!MainUtils.isEmptyValue(error?.response?.data?.message)) {
          Modal.error({
            title: error?.response?.data?.message,
          });
          return;
        } else if (
          !MainUtils.isEmptyValue(error?.response?.data?.Error?.Message)
        ) {
          const errorObj = error?.response?.data?.Error;
          const fieldErrors: Record<string, string[]> | undefined = errorObj?.Errors;
          const hasFieldErrors =
            fieldErrors && Object.keys(fieldErrors).length > 0;
          Modal.error({
            title: errorObj.Message,
            content: hasFieldErrors
              ? Object.entries(fieldErrors)
                  .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
                  .join("\n")
              : undefined,
          });
          return;
        } else if (!MainUtils.isEmptyValue(error?.response?.data)) {
          Modal.error({
            title: error?.response?.data,
          });
          return;
        } else if (!MainUtils.isEmptyValue(error?.message)) {
          Modal.error({
            title: error?.message,
          });
          return;
        }
        /* if (
          error?.response?.data?.statusCode === 401 ||
          error?.response?.data?.statusCode === 403
        ) {
          this.deleteCookie(AUTH_TOKEN);
          window.location.href = '/error-pages/error-401';
        } */
        return { error: error?.response?.data };
      },
    );
  }

  async request<T>(config: RequestConfig): Promise<any> {
    return await this.api.request<IBaseApiResponse<T>>(config);
  }
}
